import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Dialog, SelectChangeEvent } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  addProjetsToPipeline,
  getProjetbyPipeline,
  removeProjetFromPipeline,
  updateProjet,
} from '../../redux/projets/projetActions'
import { getPipelinebyId } from '../../redux/pipelines/pipelineActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { Projet } from '../../types/projetTypes'
import { UserRole } from '../../types/keycloak'
import { formatTitle } from '../../utils/formatTitle'
import { systemLabel } from '../../utils/systemLabel'
import { writeEntityNav } from '../../utils/entityNav'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatCurrency } from '../../utils/formatNumbers'
import i18n from '../../i18n'

import UseAlert from '../../components/common/alert'
import SelectMultProjet from '../projets/SelectMultProject'
import IncyiaGantt from '../../components/common/IncyiaGantt'
import {
  DetailAccordion,
  DetailButton,
  DetailEntityPanel,
  DetailInfoGrid,
  DetailInfoItem,
  DetailPageHead,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import {
  ListPageDateCell,
  ListPagePill,
  PRIORITY_PILL,
  STATUS_PILL,
} from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { priorityLabelsTranslations } from '../../utils/constants'
import '../../components/common/ListPageTable.css'
import '../../components/common/detail/detail.css'

export default function PipelineDetail() {
  const { MissionId } = useParams<{ MissionId: string }>()
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const locale = i18n.language
  const currency = useSelector(selectSpecifications)[0]?.Currency
  const roles = useSelector((state: any) => state.auth.roles)

  const projetsList = useSelector(
    (state: RootState) => state.projet.projetsbyPipelinee
  )
  const pipeline = useSelector((state: RootState) => state.pipeline.pipeline)
  const loading = useSelector((state: RootState) => state.projet.loading)

  const [openSelect, setOpenSelect] = useState(false)
  const [openSections, setOpenSections] = useState({
    info: true,
    projects: true,
  })
  const [search, setSearch] = useState('')
  const [ProjetId, setProjets] = useState<string[]>([])
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  const projetsArray = useMemo((): Projet[] => {
    if (!projetsList || typeof projetsList !== 'object') return []
    return Object.values(projetsList).filter(
      (item) =>
        typeof item === 'object' &&
        item !== null &&
        !Array.isArray(item) &&
        '_id' in (item as object)
    ) as Projet[]
  }, [projetsList])

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return projetsArray
    return projetsArray.filter((p) =>
      (p.ProjetName || '').toLowerCase().includes(q)
    )
  }, [projetsArray, search])

  const plannedBudget = useMemo(
    () => projetsArray.reduce((sum, p) => sum + (Number(p.Budget) || 0), 0),
    [projetsArray]
  )

  useEffect(() => {
    if (id) {
      dispatch(getProjetbyPipeline(id))
      dispatch(getPipelinebyId(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (!pipeline?._id) return
    writeEntityNav({
      pipelineId: pipeline._id,
      pipelineLabel: formatTitle(pipeline.PipelineName || ''),
      missionId: MissionId || pipeline.MissionId || undefined,
      missionLabel: pipeline.MissionName
        ? formatTitle(pipeline.MissionName)
        : undefined,
    })
  }, [pipeline, MissionId])

  const canManage = canManageMission(roles)
  const title = formatTitle(pipeline?.PipelineName || '') || t('Pipeline')

  const dateRangeLabel = useMemo(() => {
    if (!projetsArray.length) return null
    const starts = projetsArray
      .map((p) => (p.DateDebut ? new Date(p.DateDebut).getTime() : NaN))
      .filter((n) => !Number.isNaN(n))
    const ends = projetsArray
      .map((p) => (p.DateFin ? new Date(p.DateFin).getTime() : NaN))
      .filter((n) => !Number.isNaN(n))
    if (!starts.length || !ends.length) return null
    const from = new Date(Math.min(...starts)).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    const to = new Date(Math.max(...ends)).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    return `${from} → ${to}`
  }, [projetsArray, locale])

  const subtitle = [
    pipeline?.MissionName ? formatTitle(pipeline.MissionName) : null,
    t('pipelineDetailPage.subtitle.projects', {
      count: projetsArray.length,
      defaultValue:
        projetsArray.length === 1
          ? '1 project'
          : `${projetsArray.length} projects`,
    }),
    dateRangeLabel
      ? t('pipelineDetailPage.subtitle.runs', {
          range: dateRangeLabel,
          defaultValue: `runs from ${dateRangeLabel}`,
        })
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const toggle = (key: keyof typeof openSections) => () => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleCloseAlert = () => setAlert((a) => ({ ...a, open: false }))

  const handleProjetChange = (event: SelectChangeEvent<string[]>) => {
    setProjets(event.target.value as string[])
  }

  const handleAddProjetToPipeline = async () => {
    try {
      if (ProjetId.length && id) {
        await dispatch(addProjetsToPipeline({ ProjetId, _id: id }))
        setAlert({
          open: true,
          message: t('alerts.addProjetToPipeline.success'),
          severity: 'success',
        })
        dispatch(getProjetbyPipeline(id))
        setOpenSelect(false)
        setProjets([])
      } else {
        setAlert({
          open: true,
          message: t('alerts.addProjetToPipeline.warning'),
          severity: 'warning',
        })
      }
    } catch (error: any) {
      setAlert({
        open: true,
        message: t('alerts.addProjetToPipeline.error'),
        severity: 'error',
      })
    }
  }

  const handleDeleteClick = async (_id: string) => {
    try {
      await dispatch(
        removeProjetFromPipeline({
          projets: { projetId: _id },
          _id: id || '',
        })
      )
      dispatch(getProjetbyPipeline(id || ''))
      setAlert({
        open: true,
        message: t('alerts.onDelete.success'),
        severity: 'success',
      })
    } catch {
      setAlert({
        open: true,
        message: t('alerts.onDelete.error'),
        severity: 'error',
      })
    }
  }

  const missionId = MissionId || pipeline?.MissionId || ''

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />

      <div className="detail-page">
        <DetailPageHead
          eyebrow={t('pipelineDetailPage.eyebrow', {
            defaultValue: 'Management · Pipelines',
          })}
          title={title}
          subtitle={
            subtitle ||
            t('pipelineDetailPage.subtitle.fallback', {
              defaultValue:
                'Projects sequenced in this pipeline. Add or attach projects to build the plan.',
            })
          }
          actions={
            canManage ? (
              <>
                <DetailButton
                  variant="ghost"
                  onClick={() => setOpenSelect(true)}
                >
                  {t('pipelineDetailsPage.buttons.selectProject')}
                </DetailButton>
                <DetailButton
                  onClick={() =>
                    navigate(
                      `/mission/${missionId}/pipeline/${id}/addprojet`
                    )
                  }
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                  {t('buttons.addProject', { defaultValue: 'Add New Project' })}
                </DetailButton>
              </>
            ) : null
          }
        />

        <DetailAccordion
          title={t('pipelineDetailPage.info', { defaultValue: 'Information' })}
          open={openSections.info}
          onToggle={toggle('info')}
        >
          <DetailInfoGrid>
            <DetailInfoItem
              label={t('tables.pipelineTable.pipelineName', {
                defaultValue: 'Pipeline name',
              })}
              value={title}
            />
            <DetailInfoItem
              label={t('tables.pipelineTable.missionName', {
                defaultValue: 'Mission name',
              })}
              value={
                pipeline?.MissionName
                  ? formatTitle(pipeline.MissionName)
                  : '—'
              }
            />
            <DetailInfoItem
              label={t('pipelineDetailPage.fields.projects', {
                defaultValue: 'Projects',
              })}
              mono
              value={String(projetsArray.length)}
            />
            <DetailInfoItem
              label={t('pipelineDetailPage.fields.plannedBudget', {
                defaultValue: 'Planned budget',
              })}
              mono
              value={formatCurrency(plannedBudget, currency, {
                locale,
                useIntl: true,
              })}
            />
          </DetailInfoGrid>
        </DetailAccordion>

        <DetailAccordion
          title={t('missionDetailsPage.accordion.projects', {
            defaultValue: 'Projects',
          })}
          count={projetsArray.length}
          open={openSections.projects}
          onToggle={toggle('projects')}
        >
          <DetailEntityPanel
            listTitle={t('missionDetailsPage.sections.projectList', {
              defaultValue: 'Project list',
            })}
            searchPlaceholder={t('filterByName', {
              defaultValue: 'Filter by name',
            })}
            searchValue={search}
            onSearchChange={setSearch}
            canAdd={false}
          >
            <DetailTable<Projet>
              loading={loading && !projetsArray.length}
              loadingLabel={t('loading')}
              emptyTitle={t('missionDetailsPage.empty.title', {
                defaultValue: 'Nothing here yet',
              })}
              emptyMessage={t('pipelineDetailPage.empty.projects', {
                defaultValue:
                  'No project is attached to this pipeline yet. Create one or select an existing project.',
              })}
              rows={filteredProjects}
              rowKey={(r) => r._id as string}
              columns={[
                {
                  key: 'name',
                  header: t('tables.projectTable.projetName', {
                    defaultValue: 'Project name',
                  }),
                  render: (r) => (
                    <NavLink
                      to={`/mission/${r.MissionId || missionId}/projet/${r._id}`}
                      className="incyia-name-link"
                    >
                      <ListPageDescription
                        value={
                          r.ProjetName
                            ? formatTitle(r.ProjetName)
                            : undefined
                        }
                        tone="primary"
                      />
                    </NavLink>
                  ),
                },
                {
                  key: 'desc',
                  header: t('tables.projectTable.description', {
                    defaultValue: 'Description',
                  }),
                  render: (r) => (
                    <ListPageDescription value={r.Description} />
                  ),
                },
                {
                  key: 'start',
                  header: t('tables.projectTable.dateDebut', {
                    defaultValue: 'Start date',
                  }),
                  render: (r) => (
                    <ListPageDateCell
                      value={r.DateDebut}
                      locale={locale}
                    />
                  ),
                },
                {
                  key: 'end',
                  header: t('tables.projectTable.dateFin', {
                    defaultValue: 'End date',
                  }),
                  render: (r) => (
                    <ListPageDateCell value={r.DateFin} locale={locale} />
                  ),
                },
                {
                  key: 'priority',
                  header: t('tables.projectTable.priority', {
                    defaultValue: 'Priority',
                  }),
                  render: (r) => {
                    const priority = Number(r.Priority)
                    const style = PRIORITY_PILL[priority] ?? PRIORITY_PILL[4]
                    return (
                      <ListPagePill
                        label={
                          t(`priority.${priority}`, {
                            defaultValue:
                              priorityLabelsTranslations[priority] ||
                              style.label,
                          }) || style.label
                        }
                        bg={style.bg}
                        color={style.color}
                      />
                    )
                  },
                },
                {
                  key: 'type',
                  header: t('tables.projectTable.typeProjet', {
                    defaultValue: 'Project type',
                  }),
                  render: (r) => (
                    <ListPageDescription
                      value={
                        r.TypeProjet
                          ? systemLabel(t, 'projectType', r.TypeProjet)
                          : undefined
                      }
                    />
                  ),
                },
                {
                  key: 'status',
                  header: t('tables.projectTable.status', {
                    defaultValue: 'Status',
                  }),
                  render: (r) => {
                    const style = STATUS_PILL[r.Status || ''] ?? {
                      bg: '#EAF0F0',
                      color: '#5F7484',
                    }
                    return (
                      <ListPagePill
                        label={t(r.Status || '', {
                          defaultValue: r.Status || '—',
                        })}
                        bg={style.bg}
                        color={style.color}
                      />
                    )
                  },
                },
                {
                  key: 'budget',
                  header: t('tables.projectTable.budget', {
                    defaultValue: 'Budget',
                  }),
                  render: (r) => (
                    <span className="detail-info-val detail-info-val--mono">
                      {formatCurrency(r.Budget ?? 0, currency, {
                        locale,
                        useIntl: true,
                      })}
                    </span>
                  ),
                },
                {
                  key: 'marge',
                  header: t('tables.projectTable.marge', {
                    defaultValue: 'Margin',
                  }),
                  render: (r) =>
                    r.Marge != null ? (
                      <ListPageDescription value={String(r.Marge)} />
                    ) : (
                      <ListPagePill
                        label={t('notSet', { defaultValue: 'Not set' })}
                        bg="#EAF0F0"
                        color="#5F7484"
                      />
                    ),
                },
                {
                  key: 'source',
                  header: t('tables.projectTable.source', {
                    defaultValue: 'Source',
                  }),
                  render: (r) => <ListPageDescription value={r.Source} />,
                },
                {
                  key: 'mission',
                  header: t('tables.projectTable.missionName', {
                    defaultValue: 'Mission name',
                  }),
                  render: (r) => (
                    <ListPageDescription
                      value={
                        r.MissionName
                          ? formatTitle(r.MissionName)
                          : undefined
                      }
                    />
                  ),
                },
              ]}
              actionsHeader={t('Actions', { defaultValue: 'Action' })}
              renderActions={(r) => (
                <DetailRowActions
                  roles={roles}
                  onEdit={() => navigate(`/projet/${r._id}`)}
                  onDelete={() => handleDeleteClick(r._id as string)}
                  deleteItem={{
                    entityType: t('projects', { defaultValue: 'Project' }),
                    name: r.ProjetName
                      ? formatTitle(r.ProjetName)
                      : undefined,
                    details: [
                      {
                        label: t('deleteConfirm.fields.mission'),
                        value: r.MissionName
                          ? formatTitle(r.MissionName)
                          : '—',
                      },
                      {
                        label: t('deleteConfirm.fields.status'),
                        value: r.Status || '—',
                      },
                    ],
                  }}
                  canDeleteRoles={[UserRole.RSSI, UserRole.ADMIN]}
                />
              )}
            />
          </DetailEntityPanel>
        </DetailAccordion>

        <div className="incyia-gantt-section" style={{ marginTop: 24, width: '100%' }}>
          <IncyiaGantt
            rows={projetsArray
              .filter((p) => p.DateDebut && p.DateFin)
              .map((p) => ({
                id: p._id as string,
                name: p.ProjetName || '—',
                from: new Date(p.DateDebut as Date),
                to: new Date(p.DateFin as Date),
              }))}
            onChange={async (projectId, from, to) => {
              const existing = projetsArray.find((p) => p._id === projectId)
              await dispatch(
                updateProjet({
                  updatedProjet: {
                    ...(existing || {}),
                    DateDebut: from,
                    DateFin: to,
                  },
                  _id: projectId,
                })
              )
              if (id) await dispatch(getProjetbyPipeline(id))
            }}
          />
        </div>
      </div>

      <Dialog
        open={openSelect}
        onClose={() => setOpenSelect(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: 'incyia-modal-paper' }}
        aria-labelledby="select-project-modal"
      >
        <div className="incyia-modal-head">
          <div>
            <h3 id="select-project-modal">
              {t('popups.addProjectToPipeline.title')}
            </h3>
            <p>
              {t('popups.addProjectToPipeline.subtitle', {
                defaultValue:
                  'Link an existing project from this mission to the pipeline.',
              })}
            </p>
          </div>
          <button
            type="button"
            className="incyia-modal-x"
            onClick={() => setOpenSelect(false)}
            aria-label={t('Cancel')}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
        <div className="incyia-modal-body">
          <SelectMultProjet
            value={ProjetId}
            onChange={handleProjetChange}
            MissionId={missionId}
          />
        </div>
        <div className="incyia-modal-foot">
          <DetailButton
            variant="ghost"
            onClick={() => setOpenSelect(false)}
          >
            {t('popups.addProjectToPipeline.cancelButton')}
          </DetailButton>
          <DetailButton onClick={handleAddProjetToPipeline}>
            {t('popups.addProjectToPipeline.addButton')}
          </DetailButton>
        </div>
      </Dialog>
    </>
  )
}
